<template>
    <div class="attendance-statistics-component">

        <div id="informations">
            <table class="table">
                <tr>
                    <td>
                        <strong>{{ trans.attendance.statistics.wage_personal_number }} / {{ trans.attendance.statistics.contract_type }}</strong>
                    </td>
                    <td class="text-right">
                        <span v-if="attendance.wage_personal_number" class="badge badge-light">
                            {{ attendance.wage_personal_number }}
                        </span>
                        <span v-if="attendance.contract_type.shortcode" class="badge badge-warning ">
                            {{ attendance.contract_type.shortcode }}
                        </span>
                    </td>
                </tr>
                <tr>
                    <td><strong>{{ trans.attendance.statistics.interval_time }}</strong></td>
                    <td class="text-right">
                        {{ getBorderDates(attendance.latestValidContract.date_start, attendance.latestValidContract.date_end) }}
                    </td>
                </tr>
            </table>
        </div>

        <ul id="pills-tab" class="nav nav-pills nav-justified nav-att mt-4" role="tablist">
            <li class="nav-item">
                <a href="javascript:void(0);" class="nav-link" role="tab" @click="changeTab(1)" :class="activeTab == 1 ? 'active' : ''">
                    {{ trans.attendance.statistics.monthly_overview }}
                </a>
            </li>
            <li class="nav-item">
                <a href="javascript:void(0);" class="nav-link" role="tab" @click="changeTab(2)" :class="activeTab == 2 ? 'active' : ''">
                    {{ trans.attendance.statistics.bonuses }}
                </a>
            </li>
            <li class="nav-item" v-if="settings.workedHolidayCompensationClaim == 1">
                <a href="javascript:void(0);" class="nav-link" role="tab" @click="changeTab(3)" :class="activeTab == 3 ? 'active' : ''">
                    {{ trans.attendance.statistics.holidays }}

                    <b-badge v-if='workedHolidaysBulletVisible' variant='danger' class="ml-2 py-1">
                        <i class="fas fa-exclamation"></i>
                    </b-badge>
                </a>
            </li>
            <li class="nav-item" v-if="hasBalances()">
                <a href="javascript:void(0);" class="nav-link" role="tab" @click="changeTab(4)" :class="activeTab == 4 ? 'active' : ''">
                    {{ trans.attendance.statistics.balances }}
                </a>
            </li>
        </ul>

        <div id="pills-tabContent" class="tab-content mt-3 inline-scroll" v-if="!loading" v-cloak>

            <div class="tab-pane fade show active" role="tabpanel" v-if="activeTab == 1">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th colspan="2">{{ trans.attendance.statistics.main }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-if="settings.fundHoursType == 1">
                            <th>{{ trans.attendance.statistics.monthly_working_fund }}:</th>
                            <td>{{ formatHours(monthlyWorkingFund) }}
                                <div v-if="attendance.working_fund_type != 'monthly'">
                                    ({{ formatHours(this.attendance.working_fund_in_hours) }}/{{ this.getWorkingFundTypeLabel() }}<span v-if="this.getWorkingFundMultiplierLabel() != ''">
                                        &times;&nbsp;{{ this.getWorkingFundMultiplierLabel() }}
                                    </span>)
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>{{ trans.attendance.statistics.number_of_work_days }}:</th>
                            <td>{{ formatDays(attendance.number_of_work_days) }}</td>
                        </tr>
                        <tr>
                            <th>{{ trans.attendance.statistics.number_of_total_time }}:</th>
                            <td>{{ formatHours(attendance.number_of_total_hours) }}</td>
                        </tr>
                        <tr>
                            <th style="white-space: nowrap;">{{ trans.attendance.statistics.number_of_work_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_work_hours) }}</td>
                        </tr>
                        <tr v-if="settings.fundHoursType == 1 && settings.fundDifference == 1">
                            <th style="white-space: nowrap;">{{ trans.attendance.statistics.difference_fund }}:</th>
                            <td :style="attendance.number_of_total_hours-monthlyWorkingFund < 0 ? 'color:red;' : 'color:green;'">{{ formatHours(attendance.number_of_total_hours - monthlyWorkingFund) }}</td>
                        </tr>
                        <tr v-if="settings.plannedHours == 1">
                            <th style="white-space: nowrap;">
                                <span v-b-tooltip.hover v-b-tooltip.hover.top :title="trans.attendance.statistics.planned_hours_info">
                                    {{ trans.attendance.statistics.planned_hours }}:
                                </span>
                            </th>
                            <td :style="attendance.number_of_planned_hours < 0 ? 'color:red;' : 'color:green;'">{{ formatHours(attendance.number_of_planned_hours) }}</td>
                        </tr>
                    </tbody>
                </table>

                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th colspan="2">{{ trans.attendance.statistics.extra_time_title }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr class="sub-item" v-if="settings.allowSalaryAdditionWeekend == 1 && restaurantSettings.joinWeekendDays == 0">
                            <th>{{ trans.attendance.statistics.number_of_saturdays_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_saturdays_hours) }}</td>
                        </tr>
                        <tr class="sub-item" v-if="settings.allowSalaryAdditionWeekend == 1 && restaurantSettings.joinWeekendDays == 0">
                            <th>{{ trans.attendance.statistics.number_of_sundays_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_sundays_hours) }}</td>
                        </tr>
                        <tr class="sub-item" v-if="settings.allowSalaryAdditionWeekend == 1 && restaurantSettings.joinWeekendDays == 1">
                            <th>{{ trans.salaryAddition.weekend }}:</th>
                            <td>{{ formatHours(attendance.number_of_weekend_hours) }}</td>
                        </tr>
                        <tr class="sub-item" v-if="settings.allowOffDayWorkedHours == 1">
                            <th>{{ trans.attendance.statistics.number_of_off_days_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_off_days_hours) }}</td>
                        </tr>
                        <tr class="sub-item">
                            <th>{{ trans.attendance.statistics.number_of_night_shifts_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_night_shifts_hours) }}</td>
                        </tr>
                        <tr class="sub-item" v-if="settings.fundHoursType == 1 && settings.overtimeAllow == 1">
                            <th>{{ trans.attendance.statistics.number_of_overtime_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_overtime_hours) }}</td>
                        </tr>
                    </tbody>
                </table>

                <table
                    class="table table-striped"
                    v-if="attendance.has_any_absence"
                >
                    <thead>
                        <tr>
                            <th colspan="2">{{ trans.attendance.statistics.absence_title }}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr class="sub-item" v-if="settings.vacancyClaim == 1 && attendance.number_of_holiday_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_holiday_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_holiday_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_pn_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_pn_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_pn_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_paragraph_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_paragraph_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_paragraph_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_paragraph_family_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_paragraph_family_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_paragraph_family_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_nvp_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_nvp_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_nvp_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_compensated_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_nvp_holiday_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_compensated_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_nvn_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_nvn_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_nvn_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_not_worked_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_not_worked_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_not_worked_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_holiday_not_worked_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_holiday_not_worked_hours }}:</th>
                            <td>{{ formatHours(parseFloat(attendance.number_of_holiday_not_worked_hours)) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_ocr_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_ocr_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_ocr_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypeOcrOp == 1 && attendance.number_of_ocr_op_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_ocr_op_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_ocr_op_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypeBloodDonation == 1 && attendance.number_of_blood_donation_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_blood_donation_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_blood_donation_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_absence_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_absence_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_absence_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypeMeeting == 1 && attendance.number_of_meeting_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_meeting_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_meeting_hours) }}</td>
                        </tr>
                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 &&
                        settings.allowWorkIntervalTypeHomeOffice == 1 && attendance.number_of_home_office_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_home_office_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_home_office_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypeFuneral == 1 && attendance.number_of_funeral_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_funeral_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_funeral_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypeCourse == 1 && attendance.number_of_course_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_course_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_course_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 &&
                        settings.allowWorkIntervalTypeMedicalCheckup == 1 && attendance.number_of_medical_checkup_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_medical_checkup_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_medical_checkup_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 &&
                        settings.allowWorkIntervalTypeAdditionalHoliday == 1 && attendance.number_of_additional_holiday_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_additional_holiday_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_additional_holiday_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_pvpz_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_pvpz_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_pvpz_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_pvpz60_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_pvpz60_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_pvpz60_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_pvpz80_hours !=0">
                            <th>{{ trans.attendance.statistics.number_of_pvpz80_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_pvpz80_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="attendance.number_of_wedding_hours != 0">
                          <th>{{ trans.attendance.statistics.number_of_wedding_hours }}:</th>
                          <td>{{ formatHours(attendance.number_of_wedding_hours) }}</td>
                        </tr>

                        <tr class="sub-item" v-if="settings.dynamicWorkIntervalTypesAllow == 1 && settings.allowWorkIntervalTypePaternityLeave == 1 && attendance.number_of_paternity_leave_hours != 0">
                            <th>{{ trans.attendance.statistics.number_of_paternity_leave_hours }}:</th>
                            <td>{{ formatHours(attendance.number_of_paternity_leave_hours) }}</td>
                        </tr>
                    </tbody>
                </table>

                <table class="table table-striped" v-if="legalChecksEnabled">
                    <thead>
                        <tr>
                            <th>{{ trans.attendance.statistics.legal_checks_title }}</th>
                            <th class="text-right">
                                <span class="bg-white p-1 text-white mr-1" style="border-radius: 3px;" v-if="!legalChecksDataFinal" v-b-tooltip.hover :title="trans.attendance.statistics.legal_checks_sync_warning">
                                    <i class="text-danger fas fa-clock"></i>
                                </span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(item,key) in legalChecks" class="sub-item text-danger">
                            <td>{{ item.message }}</td>
                            <td>
                                <a v-on:click="deleteLegalCheck(item.id, $event)" class="btn btn-sm btn-secondary" v-if="item.deleteable">
                                    <i class="fa fa-trash-alt"></i>
                                </a>
                            </td>
                        </tr>
                        <tr v-if="legalChecks.length === 0">
                            <td colspan="2" class="text-center text-muted py-3">{{ trans.attendance.statistics.legal_checks_empty }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="tab-pane fade show active" role="tabpanel" v-if="activeTab == 2">
                <personal-bonuses
                    :attendance="attendance"
                    :trans="trans"
                    :restaurant-currency="restaurantCurrency"
                    :contract-settings="settings"
                    :bonuses-url-store="bonusesUrlStore"
                    :bonuses-url-destroy="bonusesUrlDestroy"
                />

                <variable-wages
                    v-if="settings.salaryVariableWageAllow == 1"
                    :varWageAmount="attendance.salary_variable_wage"
                    :varWageIsModified="attendance.salary_variable_wage_is_modified"
                    :varWageMonthly="attendance.monthly_salary_variable_wage"
                    :workedHoursTotal="attendance.number_of_work_hours_with_overtime"
                    :restaurant-currency="restaurantCurrency"
                    :contract-settings="settings"
                    :trans="trans"
                    :var-wage-url-save="varWageUrlSave"
                    :var-wage-url-unset="varWageUrlUnset"
                />

                <dynamic-addition
                    v-if="settings.dynamicAdditionsAllow == 1"
                    v-for="(item, key) in dynamicAdditions"
                    v-bind:key="'dynaddition-'+key"
                    :item="item"
                    :restaurant-currency="restaurantCurrency"
                    :contract-settings="settings"
                    :workedHoursTotal="attendance.number_of_work_hours_with_overtime"
                    :trans="trans.attendance.dynamic_additions"
                    :url-save="dynamicAdditionUrlSave.replace('#type#',key)"
                    :url-unset="dynamicAdditionUrlUnset.replace('#type#',key)"
                    @refreshdata="loadDynamicAdditionsData"
                />

                <deposits
                    v-if="settings.depositAllow == 1"
                    :deposits="attendance.deposits"
                    :depositsSum="attendance.monthly_deposit"
                    :restaurant-currency="restaurantCurrency"
                    :trans="trans"
                    :deposit-url-store="depositUrlStore"
                    :deposit-url-destroy="depositUrlDestroy"
                />

                <extra-shifts
                    :days="days"
                    :trans="trans"
                    :restaurant-currency="restaurantCurrency"
                    :settings="settings"
                    :attendance="attendance"
                />
            </div>

            <div class="tab-pane fade show active holidays-table-wrapper" role="tabpanel"
                 v-if="settings.workedHolidayCompensationClaim == 1 && activeTab == 3"
            >
                <holidays
                    :employee-contract-id="attendance.id"
                    :days="workedHolidays"
                    :trans="trans"
                    :settings="settings"
                    :selected-month="selectedMonth"
                    holidays-refresh-url="/worked-holidays/get-data"
                    @refreshworkedholidays="refreshWorkedHolidays"
                />
            </div>

            <div class="tab-pane fade show active holidays-table-wrapper" role="tabpanel" v-if="activeTab == 4">
                <balances
                        :attendance="attendance"
                        :employee-contract-id="attendance.id"
                        :trans="trans"
                        :selected-month="selectedMonth"
                        :balances="balances"
                        :country="restaurantCountry"
                />
            </div>

        </div>
    </div>
</template>


<script>
    import {formatDays, formatHours, formatMoney} from "../../utils";
export default {
        name: 'attendance-statistics',
        data: function() {
            return {
                activeTab: 1,
                momentSelectedMonth: null,
                dynamicAdditions: []
            };
        },

        props: {
            'attendance': {
                type: Object
            },
            'balances': {
                type: Object
            },
            'days': {
                type: Array
            },
            'loading': {
                type: Boolean
            },
            'workingFundInHours': {
                type: Number
            },
            'dailyWorkingFundInHours': {
                type: Number
            },
            'monthlyWorkingFund': {
                type: Number
            },
            'workingDays': {
                type: Number
            },
            'selectedMonth': {
                type: String
            },
            'trans': {
                type: Object
            },
            'bonusesUrlStore': {
                type: String
            },
            'bonusesUrlDestroy': {
                type: String
            },
            'depositUrlStore': {
                type: String
            },
            'depositUrlDestroy': {
                type: String
            },
            'varWageUrlSave': {
                type: String
            },
            'varWageUrlUnset': {
                type: String
            },
            'dynamicAdditionUrlSave': {
                type: String
            },
            'dynamicAdditionUrlUnset': {
                type: String
            },
            'dynamicAdditionUrlLoad': {
                type: String
            },
            'restaurantCurrency': {
                type: String
            },
            'restaurantCountry': {
              type: String
            },
            'settings': {
                type: Object
            },
            'restaurantSettings': {
                type: Object
            },
            'workedHolidays': {
                type: Array
            },
            'legalChecksEnabled': {
                type: Boolean
            },
            'legalChecks': {
                type: Array
            },
            'legalChecksDataFinal': {
                type: Boolean
            },
            'legalChecksUrlDelete': {
                type: String
            },
        },

        created() {
            this.momentSelectedMonth = moment(this.selectedMonth, 'YYYY-MM');
            this.loadActiveTab();
        },

        mounted() {
            this.loadDynamicAdditionsData();
        },

        methods: {
            formatHours(x) {
                return formatHours(x);
            },
            formatMoney(x) {
                return formatMoney(x, this.restaurantCurrency);
            },
            formatDays(x) {
                return formatDays(x);
            },
            changeTab(tabNum) {
                this.activeTab = tabNum;
                this.saveActiveTab();
            },
            saveActiveTab() {
                const key = 'attendance-active-tab';
                localStorage.setItem(key, this.activeTab);
            },
            loadActiveTab() {
                const key = 'attendance-active-tab';
                const savedTab = localStorage.getItem(key);
                if (savedTab !== null && this.isTabValid(parseInt(savedTab))) {
                    this.activeTab = parseInt(savedTab);
                } else {
                    this.activeTab = 1;
                }
            },
            isTabValid(tabIndex) {
                // Check if the tab exists for the current employee
                if (tabIndex === 1 || tabIndex === 2) {
                    return true; // Monthly overview and bonuses are always available
                }
                if (tabIndex === 3) {
                    return this.settings.workedHolidayCompensationClaim == 1;
                }
                if (tabIndex === 4) {
                    return this.hasBalances();
                }
                return false;
            },
            refreshBalances() {
              this.$emit('refreshbalances');
            },
            refreshWorkedHolidays() {
                this.$emit('refreshworkedholidays');
            },
            refreshCalendar(withLoading = true) {
                this.$emit('refreshcalendar', withLoading);
            },
            refreshAttendance(withLoading = true) {
                this.$emit('refreshattendance', withLoading);
            },
            getWorkingFundTypeLabel()
            {
                switch(this.attendance.working_fund_type)
                {
                    case 'monthly':
                        return this.trans.attendance.statistics.working_fund_monthly;
                        break;

                    case 'weekly':
                        return this.trans.attendance.statistics.working_fund_weekly;
                        break;

                    case 'daily':
                        return this.trans.attendance.statistics.working_fund_daily;
                        break;
                }
                return "";
            },
            getWorkingFundMultiplierLabel()
            {
                switch(this.attendance.working_fund_type)
                {
                    case 'weekly':
                        return "";
                        break;

                    case 'daily':
                        return this.workingDays+" "+this.trans.common.days;
                        break;
                }
                return "";
            },
            getMonthLabel() {
                let splitedDate = this.selectedMonth.split('-')
                let month = this.trans['months'][parseInt(splitedDate[1])].charAt(0).toUpperCase() + this.trans['months'][parseInt(splitedDate[1])].slice(1);
                let year = splitedDate[0];

                return month+" "+year;
            },
            getBorderDates(start, end) {
                let start_date = new Date(start);
                let end_date = end != null ? new Date(end) : '';
                let final_start_date = start_date.getDate() + '.' + (start_date.getMonth()+1) + '.' + start_date.getFullYear();
                let final_end_date = end_date != '' ? ' - ' + end_date.getDate() + '.' + (end_date.getMonth()+1) + '.' + end_date.getFullYear() : ' - ...';

                return final_start_date + final_end_date;
            },

            loadDynamicAdditionsData() {
                axios.get(this.dynamicAdditionUrlLoad).then((response) => {
                    this.dynamicAdditions = response.data;
                },
                (error) => {
                    _this.$notify({ type: 'error', title: 'Error!', text: error.response.data.message ? error.response.data.message : 'An error has occured.' });
                });
            },

            deleteLegalCheck(id, evt) {
                evt.target.classList.add('disabled');

                let url = this.legalChecksUrlDelete.replace("%id%", id);
                axios.delete(url).then((response) => {
                    this.$emit('refreshlegalchecks');
                    evt.target.classList.remove('disabled');
                }, (error) => {
                    this.$emit('refreshlegalchecks');
                    evt.target.classList.remove('disabled');
                });
            },

            hasBalances() {
                return this.settings.vacancyClaim == 1 && Object.keys(this.balances).length > 0;
            }
        },

        computed: {
            workedHolidaysBulletVisible()
            {
                const nvpHolidays = this.workedHolidays.filter(x => x.compensation_type == 2 && !x.compensated_by);
                return nvpHolidays && nvpHolidays.length > 0;
            }

        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/show/AttendanceStatistics.vue